/* WHO ALREADY SUPPLIES THIS GYM — and how big the untouched half of the TAM is.
 *
 * Brian: "did you do the team jewelry HJ scrape to get them into our cheer tam"
 * Brian, earlier: "i need you to go through their accounts to create more of a
 * tam for our gym partnership opportunites that we have to target. this lives
 * in command center."
 *
 * Herff Jones publishes every cheer account it holds on one public storefront
 * page. `src/data/hj_cheer_accounts.json` is that page, captured and typed
 * (see tools/gen-hj-cheer-tam.mjs for how, and for the truncation trap that
 * nearly shipped two thirds of a list as the whole thing).
 *
 * ⚠️ A NAME ON THAT PAGE IS NOT AN INCUMBENT — this was the build's first and
 * worst mistake, and Brian caught it: "They each aren't HJ incumbents because
 * some of them are from 2022 and we've picked up a lot of new accounts."
 *
 * The page lists every gym Herff Jones has EVER set up a storefront for, not
 * every gym it supplies now. Measured: **77 of 228 gym collections hold ZERO
 * products** and 57 more have published nothing in over two years. So the
 * evidence is graded from what is IN the collection (`tierMeta` below), and
 * only ACTIVE licenses "they are Herff Jones's customer".
 *
 * That produces three piles, not two, and the third is the interesting one:
 *
 *   ACTIVE + we hold it   → a live competitive loss inside our own CRM.
 *   ACTIVE + we don't     → expansion against a real incumbent.
 *   STALE / EMPTY         → ⚠️ A BUYING SIGNAL. Their relationship appears to
 *                           have ended, and per Brian we have won a lot of
 *                           these since. Treating it as "theirs" would have a
 *                           rep conceding an account we may already hold.
 *
 * ⚠️ AND IT WRITES NOTHING. The list is read at RUNTIME and matched in the
 * browser, exactly like `rep_proficiency.json` — so refreshing the capture is a
 * re-bundle of one file, and no HubSpot record is ever stamped with a claim
 * sourced from a competitor's marketing page. If SR ever wants `sr_incumbent`
 * on the record, that is a deliberate, gated write, not a side effect of a
 * scrape.
 *
 * ⚠️ PURE — IT TAKES THE LIST, IT DOES NOT IMPORT IT. Same split as
 * `assignmentModel.js` / `assignments.js`: `cheerIncumbentData.js` is the only
 * file that loads the JSON. That keeps this module drivable by
 * `verify-cheer-incumbent.mjs` under plain Node (a bare JSON import is a Vite
 * affordance, not a Node one) and, more usefully, means a refreshed capture is
 * a data swap rather than a code change.
 *
 * No React, no fetch.
 */

export const HERFF_JONES = 'Herff Jones'

/* ⚠️ NORMALIZATION IS CASE / PUNCTUATION / SPACING ONLY, and that restraint is
 * the whole design. Cheer names collide relentlessly — "Champion Allstars",
 * "Champion Cheer Allstars", "Champions All Star Cheerleading" and "Champions
 * Allstar Cheerleading" are FOUR separate businesses on the source page. Strip
 * the filler words "cheer" and "all stars" to be helpful and all four become one
 * key, which would report three real targets as already covered.
 *
 * This is the same failure that once produced three schools named "Cedar
 * Rapids" in the state rosters. A near-match is offered to a human below; it is
 * never resolved into an answer here. */
export function normalizeGym(v) {
  return String(v ?? '')
    .toLowerCase()
    .replace(/[‘’']/g, '')
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ')
}

/* The loose key exists ONLY to surface a "did you mean" for a person to judge.
 * Nothing in this module lets it decide. */
const FILLER = new Set([
  'cheer', 'cheerleading', 'allstar', 'allstars', 'all', 'star', 'stars',
  'athletics', 'elite', 'the', 'and', 'gym', 'academy',
])
function looseKey(v) {
  const parts = normalizeGym(v).split(' ').filter((w) => w && !FILLER.has(w))
  /* ⚠️ A NAME THAT IS ENTIRELY FILLER GETS NO LOOSE KEY. "All Star Cheer"
   * reduces to nothing, and an empty key would collide with every other
   * all-filler name — turning the weakest possible signal into a suggestion
   * that looks as confident as any other. */
  if (parts.length < 1) return ''
  return parts.sort().join(' ')
}

/**
 * Build the matcher over a captured competitor list.
 *
 * ⚠️ ONLY `type: 'gym'` ROWS ARE INDEXED. The source page also carries school
 * programs, universities, an NFL squad, a CFL club, a drum corps and ICU
 * national teams — real opportunities, but none of them can take a GYM
 * PARTNERSHIP (a contract plus an exclusive branded jewelry program at the
 * gym), so matching a cheer gym against them could only ever invent a false
 * incumbent.
 */
export function buildIncumbentIndex(dataset = {}) {
  const gyms = (dataset.accounts || []).filter((a) => a.type === 'gym')
  const exact = new Map()
  const loose = new Map()
  for (const a of gyms) {
    const k = normalizeGym(a.name)
    if (k && !exact.has(k)) exact.set(k, a)
    const l = looseKey(a.name)
    if (!l) continue
    if (!loose.has(l)) loose.set(l, [])
    loose.get(l).push(a)
  }
  return { gyms, exact, loose, meta: dataset.meta || {} }
}

/**
 * What do we know about who supplies this gym?
 *
 * @returns {{
 *   incumbent: string|null,   // 'Herff Jones' only on an EXACT match
 *   confidence: 'confirmed'|'possible'|'unknown',
 *   evidenceUrl: string,      // their storefront for this account — the receipt
 *   matchedName: string,      // the name as HERFF JONES spells it
 *   candidates: string[],     // near matches, for a human, when not confirmed
 *   reading: string,
 * }}
 */
/* ⚠️ A LINE ITEM OVER THIS IS A TEAM PACKAGE OR DEPOSIT, NOT A RING. The
 * highest genuine single-ring price on the whole storefront is $825. Reporting
 * a $10,430 package as a gym's ring price would rank it as the most premium
 * buyer on the board off one bulk invoice. */
export const PACKAGE_PRICE = 1000

/** Does this gym have a real per-ring price, or only a package line? */
export const hasRingPrice = (a) => a?.medPrice != null && a.medPrice <= PACKAGE_PRICE

/**
 * What each activity tier licenses us to claim.
 *
 * ⚠️ ONLY `ACTIVE` RETURNS AN INCUMBENT. The other three are the correction:
 * a parked page and a two-year-old page are not evidence of a current vendor,
 * and STALE in particular is a BUYING SIGNAL rather than a warning — it is a
 * gym whose competitor relationship appears to have ended.
 */
export function tierMeta(a) {
  const when = a?.lastPublished ? ` Last new ring ${a.lastPublished}.` : ''
  switch (a?.tier) {
    case 'ACTIVE':
      return {
        incumbent: HERFF_JONES,
        confidence: 'confirmed',
        reading: `Herff Jones added product here within the last year — ${a.products} live on their storefront.${when} Treat them as the incumbent.`,
      }
    case 'COOLING':
      return {
        incumbent: null,
        confidence: 'cooling',
        reading: `On the Herff Jones storefront, but nothing new in 1–2 years.${when} Could still be theirs or could have lapsed — worth a call, not an assumption.`,
      }
    case 'STALE':
      return {
        incumbent: null,
        confidence: 'lapsed',
        reading: `Herff Jones has published nothing new here in over two years.${when} Likely a finished relationship — and if SR already holds this gym, we may have won it since.`,
      }
    default:
      return {
        incumbent: null,
        confidence: 'empty',
        reading: 'Herff Jones has a collection page for this gym with NO products in it. Nobody can order from it, so it is not evidence of a current vendor.',
      }
  }
}

export function incumbentFor(gym, index) {
  const { exact: EXACT, loose: LOOSE } = index || { exact: new Map(), loose: new Map() }
  const name = typeof gym === 'string' ? gym : (gym?.name || gym?.company || '')
  const blank = {
    incumbent: null, confidence: 'unknown', tier: null,
    products: 0, medPrice: null, minPrice: null, maxPrice: null,
    lastPublished: null, evidenceUrl: '', matchedName: '', candidates: [],
    reading: 'Not on the Herff Jones storefront. That is not proof they are unsupplied — only that this one competitor does not list them.',
  }
  if (!name) return blank

  const hit = EXACT.get(normalizeGym(name))
  if (hit) {
    /* ⚠️ THE TIER DECIDES WHAT WE ARE ENTITLED TO SAY, and this replaces a flat
     * claim that was wrong for more than half the list.
     *
     * Brian: "They each aren't HJ incumbents because some of them are from 2022
     * and we've picked up a lot of new accounts."
     *
     * This used to return `incumbent: HERFF_JONES` for any name on the page,
     * reading "Herff Jones runs a live ordering storefront for this gym. We are
     * not their ring vendor today." For 77 gyms that storefront holds ZERO
     * products, and for 57 more nothing has been added in over two years — so
     * the sentence asserted a live competitor relationship AND conceded a loss,
     * on evidence that supported neither. A rep opening a call on it would be
     * telling a customer we may already have that they buy from someone else. */
    const m = tierMeta(hit)
    return {
      incumbent: m.incumbent,
      confidence: m.confidence,
      tier: hit.tier || 'EMPTY',
      products: hit.products ?? 0,
      medPrice: hit.medPrice ?? null,
      minPrice: hit.minPrice ?? null,
      maxPrice: hit.maxPrice ?? null,
      lastPublished: hit.lastPublished || null,
      evidenceUrl: hit.incumbentUrl,
      matchedName: hit.name,
      candidates: [],
      reading: m.reading,
    }
  }

  const near = LOOSE.get(looseKey(name)) || []
  if (near.length) {
    return {
      ...blank,
      confidence: 'possible',
      candidates: near.map((a) => a.name),
      /* ⚠️ `incumbent` STAYS NULL HERE ON PURPOSE. A possible is a question for
       * a person, and a rep who opens a call with "we know you use Herff Jones"
       * on a guess has spent the credibility the whole pitch runs on. */
      reading: `Close to ${near.length === 1 ? 'an account' : 'accounts'} on the Herff Jones list, but not an exact name match — confirm before treating it as an incumbent.`,
    }
  }
  return blank
}

/** Convenience for a badge. */
export const isHerffJones = (gym, index) => incumbentFor(gym, index).confidence === 'confirmed'

/**
 * The TAM split across the gyms SR actually holds.
 *
 * ⚠️ REPORTS THE UNHELD REMAINDER TOO, because that is the number Brian asked
 * for. A count of how many of OUR gyms are lost says nothing about how many of
 * THEIR gyms we have never created a record for — and the second is the larger
 * pile by a wide margin.
 */
export function incumbentRollup(gyms = [], index) {
  const idx = index || { gyms: [], exact: new Map(), loose: new Map(), meta: {} }
  const HJ_META = idx.meta || {}
  let confirmed = 0
  let possible = 0
  let lapsed = 0
  const seen = new Set()
  for (const g of gyms) {
    const r = incumbentFor(g, idx)
    /* ⚠️ EVERY EXACT MATCH IS "SEEN", whatever its tier. `seen` answers "do we
     * hold a record for this name", which is independent of who supplies them —
     * so a stale gym must not fall back into the expansion list as if we had
     * never heard of it. Tier decides what we SAY; the match decides what we
     * HOLD, and conflating the two double-counts the TAM. */
    if (r.matchedName) seen.add(normalizeGym(r.matchedName))
    if (r.confidence === 'confirmed') confirmed += 1
    else if (r.confidence === 'possible') possible += 1
    else if (r.confidence === 'lapsed' || r.confidence === 'empty') lapsed += 1
  }
  const notHeld = idx.gyms.filter((a) => !seen.has(normalizeGym(a.name)))
  return {
    ours: gyms.length,
    theirs: idx.gyms.length,
    /* Ours, on their list, and Herff Jones is ACTIVELY selling them — a live
     * competitive loss sitting in our own CRM. */
    confirmed,
    possible,
    /* ⚠️ Ours, on their list, but the storefront is empty or years cold. NOT a
     * loss — the most likely reading is that we already won it. */
    lapsed,
    /* Theirs, with no record on our side — the expansion list. */
    notHeld: notHeld.length,
    notHeldAccounts: notHeld,
    source: HJ_META.source || '',
    captured: HJ_META.captured || '',
  }
}
